import { X, PanelLeftClose, PanelLeft, LogOut } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { adminSilentApi } from '../../utils/apiInstances';
import { useSidebar, useAuth } from '../app/providers';
import logo from '../pages/assets/logo.svg';
import { parseListResponse } from '../shared/utils';

import sidebarConfig from './sidebarConfig';
import SidebarGroup from './SidebarGroup';
import SidebarItem from './SidebarItem';



const Sidebar: React.FC = () => {
    const { isCollapsed, isMobileOpen, toggleCollapse, closeMobile } = useSidebar();
    const { logout } = useAuth();
    const [badgeCounts, setBadgeCounts] = useState<Record<string, number | undefined>>({});

    useEffect(() => {
        let isActive = true;

        const fetchPendingCounts = async () => {
            try {
                const gameParams = new URLSearchParams({
                    host_status: 'submitted',
                    page: '1',
                    per_page: '1',
                });

                const pitchParams = new URLSearchParams({
                    status: 'pending',
                    page: '1',
                    per_page: '1',
                });
                pitchParams.append('status', 'submitted');

                const [gameResponse, pitchResponse] = await Promise.all([
                    adminSilentApi('GET', `/admin/admin-game-approvals?${gameParams.toString()}`),
                    adminSilentApi('GET', `/admin/admin-pitch-approvals?${pitchParams.toString()}`),
                ]);

                const gamePending = parseListResponse(gameResponse).total || 0;
                const pitchPending = parseListResponse(pitchResponse).total || 0;

                if (isActive) {
                    setBadgeCounts({
                        'organizer-games': gamePending,
                        'organizer-game-approvals': gamePending,
                        'organizer-pitches': pitchPending,
                        'organizer-pitch-approvals': pitchPending,
                    });
                }
            } catch {
                if (isActive) {
                    setBadgeCounts({});
                }
            }
        };

        void fetchPendingCounts();
        const intervalId = globalThis.setInterval(fetchPendingCounts, 60_000);

        return () => {
            isActive = false;
            globalThis.clearInterval(intervalId);
        };
    }, []);

    return (
        <>
            {/* Mobile overlay */}
            {isMobileOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={closeMobile}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
          fixed top-0 left-0 z-50 h-screen bg-white border-r border-slate-200
          transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-[72px]' : 'w-64'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
            >
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className={`flex items-center h-16 px-4 border-b border-slate-200 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
                        {!isCollapsed && (
                            <Link to="/" className="flex items-center">
                                <img src={logo} alt="SACA" className="h-8" />
                            </Link>
                        )}

                        {/* Mobile close */}
                        <button
                            onClick={closeMobile}
                            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                        >
                            <X size={20} />
                        </button>

                        {/* Desktop collapse toggle */}
                        <button
                            onClick={toggleCollapse}
                            className="hidden lg:flex p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            {isCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
                        </button>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                        {sidebarConfig.map((item) => {
                            if (item.type === 'group') {
                                return <SidebarGroup key={item.id} group={item} badgeCounts={badgeCounts} />;
                            }
                            return <SidebarItem key={item.id} item={item} badgeCount={badgeCounts[item.id]} />;
                        })}
                    </nav>

                    {/* Footer */}
                    <div className="p-3 border-t border-slate-200">
                        <button
                            onClick={logout}
                            className={`
                flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
                text-slate-600 hover:bg-red-50 hover:text-red-600
                transition-colors
                ${isCollapsed ? 'justify-center' : ''}
              `}
                        >
                            <LogOut size={18} />
                            {!isCollapsed && <span className="font-medium text-sm">Sign Out</span>}
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
